import { useState, useEffect } from 'react';
import AdminLayout from '../components/admin/AdminLayout';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { fmtTRY } from '../utils/formatCurrency';
import { BarChart3, Users, BookOpen, CreditCard, Award } from 'lucide-react';
import PageHeader from '../components/admin/PageHeader';
import axios from 'axios';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminReports() {
  const { lang } = useLanguage();
  const { authHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { axios.get(`${API}/admin/reports`, { headers: authHeaders() }).then(r => setData(r.data)).catch(console.error).finally(() => setLoading(false)); }, [authHeaders]);

  const pageHeaderEl = (
    <PageHeader
      breadcrumbs={[
        { label: 'Admin', href: '/admin' },
        { label: lang === 'tr' ? 'Finans' : 'Finance' },
        { label: lang === 'tr' ? 'Raporlar' : 'Reports' },
      ]}
      title={lang === 'tr' ? 'Raporlar' : 'Reports'}
      subtitle={lang === 'tr' ? 'Platform geneli istatistikler ve dağılımlar' : 'Platform-wide statistics and distributions'}
    />
  );

  if (loading) return <AdminLayout>{pageHeaderEl}<div className="flex justify-center py-20"><div className="w-10 h-10 border-4 border-[var(--brand-primary)] border-t-transparent rounded-full animate-spin" /></div></AdminLayout>;

  return (
    <AdminLayout>
      {pageHeaderEl}
      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
        {[
          { label: lang === 'tr' ? 'Kullanici' : 'Users', value: data?.summary?.users, icon: Users, color: '#0EA5E9' },
          { label: lang === 'tr' ? 'Kurs' : 'Courses', value: data?.summary?.courses, icon: BookOpen, color: '#10B981' },
          { label: lang === 'tr' ? 'Kayıt' : 'Enrollments', value: data?.summary?.enrollments, icon: Users, color: '#E5B400' },
          { label: lang === 'tr' ? 'Ödeme' : 'Payments', value: data?.summary?.payments, icon: CreditCard, color: '#FF4B4B' },
          { label: lang === 'tr' ? 'Yorum' : 'Reviews', value: data?.summary?.reviews, icon: BarChart3, color: '#0EA5E9' },
          { label: lang === 'tr' ? 'Sertifika' : 'Certificates', value: data?.summary?.certificates, icon: Award, color: '#10B981' },
          { label: lang === 'tr' ? 'Gelir' : 'Revenue', value: fmtTRY(data?.summary?.revenue || 0), icon: CreditCard, color: '#E5B400' },
        ].map((s, i) => (
          <div key={i} className="card-clay p-3 text-center">
            <s.icon size={18} className="mx-auto mb-1" style={{ color: s.color }} />
            <p className="font-display text-lg font-bold">{s.value}</p>
            <p className="text-[10px] text-[var(--text-muted)] font-semibold">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Users by Role */}
        <div className="card-clay p-5">
          <h3 className="font-display font-bold mb-4">{lang === 'tr' ? 'Kullanici Rolleri' : 'User Roles'}</h3>
          {Object.entries(data?.users_by_role || {}).map(([role, count]) => (
            <div key={role} className="flex items-center gap-3 mb-3">
              <span className="text-sm font-semibold capitalize w-24">{role}</span>
              <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(count / (data?.summary?.users || 1)) * 100}%`, backgroundColor: role === 'admin' ? '#FF4B4B' : role === 'instructor' ? '#0EA5E9' : '#10B981' }} /></div>
              <span className="font-bold text-sm w-8 text-right">{count}</span>
            </div>
          ))}
        </div>

        {/* Courses by Category */}
        <div className="card-clay p-5">
          <h3 className="font-display font-bold mb-4">{lang === 'tr' ? 'Kategori Dagilimi' : 'Categories'}</h3>
          {(data?.courses_by_category || []).map((c, i) => (
            <div key={i} className="flex items-center justify-between py-2 border-b border-[var(--border-light)] last:border-0">
              <span className="text-sm font-semibold capitalize">{c.category}</span>
              <div className="flex items-center gap-3">
                <span className="text-xs text-[var(--text-muted)]">{c.count} {lang === 'tr' ? 'kurs' : 'courses'}</span>
                <span className="text-xs font-bold text-[var(--brand-primary)]">{lang === 'tr' ? 'Ort.' : 'Avg.'} {fmtTRY(c.avg_price)}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Top Courses */}
        <div className="card-clay p-5 lg:col-span-2">
          <h3 className="font-display font-bold mb-4">{lang === 'tr' ? 'En Popüler Kurslar' : 'Top Courses'}</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b-2 border-[var(--border-light)]">
                <th className="text-left pb-2 font-bold">#</th><th className="text-left pb-2 font-bold">{lang === 'tr' ? 'Kurs' : 'Course'}</th>
                <th className="text-right pb-2 font-bold">{lang === 'tr' ? 'Öğrenci' : 'Students'}</th><th className="text-right pb-2 font-bold">{lang === 'tr' ? 'Puan' : 'Rating'}</th>
              </tr></thead>
              <tbody>
                {(data?.top_courses || []).map((c, i) => (
                  <tr key={i} className="border-b border-[var(--border-light)]">
                    <td className="py-2 font-bold text-[var(--brand-primary)]">{i + 1}</td>
                    <td className="py-2 font-semibold">{c.title}</td>
                    <td className="py-2 text-right font-bold">{c.enrolled}</td>
                    <td className="py-2 text-right font-bold text-[var(--brand-secondary-shadow)]">{c.rating}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
